import {
  type AccessorKeyColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Header,
  type RowData,
  useReactTable,
} from '@tanstack/react-table';
import { IconArrowNarrowDown, IconArrowNarrowUp } from '@tabler/icons-react';
import { cn } from '../../lib/utils';
import { handleExport } from './export';
import {
  Table as TablePrimitive,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './components/Table';
import { Tooltip, TooltipContainerContext } from '../Tooltip';
import type {
  TableProps,
  TableRef,
  TableComponent,
  TableContextType,
} from './types';

import { Filter } from './components/Filter/Filter';
import { TableContext } from './hooks/useTableContext';
import { usePersistentTableState } from './hooks/usePersistentTableState';
import {
  useFirstPageOnChange,
  useNearestValidPage,
} from '../../hooks/usePageBounds';
import {
  forwardRef,
  Fragment,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ConditionalWrapper } from '../../lib/ConditionalWrapper';
import { arrIncludesArrayItem, numberInRange } from './filterFn';
import {
  getAlignmentClass,
  getAriaSort,
  getFlexAlignmentClass,
  resolveTableUpdater,
} from './utils';
import useI18n from '../../hooks/useI18n';
import { Footer } from './components/Footer';
import {
  clearActiveFullscreenTable,
  setActiveFullscreenTable,
} from './fullscreen';

function TableInner<TData extends RowData>(
  {
    children,
    columns,
    data,
    id,
    renderExpandable = () => null,
    tableContentEndSlot,
    disableExport = false,
    exportOptions,
    exportFn,
    className,
    containerClassName,
    pageSize = 50,
    rowsPerPageOptions = [10, 20, 50],
    hideFooter = false,
    paginationLabel,
    globalFilterFn = 'includesString',
    onStateChange,
    initialState,
    expanded,
    manualPagination,
    manualFiltering,
    manualSorting,
    rowCount,
    ...rest
  }: TableProps<TData>,
  ref: Ref<TableRef<TData>>,
) {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  // One page of rows cannot be exported as the whole table.
  const exportDisabled = disableExport || (!!manualPagination && !exportFn);
  // The root node that becomes a fixed, z-10000 stacking context in fullscreen.
  // Tracked as state (via a callback ref) so popups/dialogs can portal into it
  // and re-render once it's mounted, stacking above the table instead of behind.
  const [fullscreenContainer, setFullscreenContainer] =
    useState<HTMLDivElement | null>(null);

  exportOptions = { filename: 'export', ...exportOptions };

  const tableId = useMemo(() => {
    return (
      `${window.location.pathname}_${id}_` +
      columns
        .map((column) => (column as AccessorKeyColumnDef<TData>)?.accessorKey)
        .sort()
        .join('_')
    );
  }, [id, columns]);

  const firstSortableColumn = useMemo(
    () => columns.find((column) => column.enableSorting !== false),
    [columns],
  );

  const state = {
    columnFilters: [],
    sorting: firstSortableColumn
      ? [
          {
            id: (firstSortableColumn?.id ??
              (firstSortableColumn as AccessorKeyColumnDef<TData, unknown>)
                ?.accessorKey) as string,
            desc: true,
          },
        ]
      : [],
    columnVisibility: {},
    ...initialState,
  };

  const [pagination, setPagination] = useState<{
    pageIndex: number;
    pageSize: number;
  }>({
    pageIndex: 0,
    pageSize,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const container = isFullscreen ? fullscreenContainer : undefined;
  const [expandedRows, setExpandedRows] = useState({});
  const [tableState, setTableState] = usePersistentTableState(tableId, state, {
    restore: (storedState, initialState) => {
      const filters = [
        ...(storedState.columnFilters ?? []),
        ...initialState.columnFilters,
      ];
      const columnFilters = Array.from(
        new Map(filters.map((item) => [item.id, item])).values(),
      );

      return {
        sorting: storedState.sorting ?? initialState.sorting,
        columnFilters,
        columnVisibility:
          storedState.columnVisibility ?? initialState.columnVisibility,
      };
    },
  });

  useEffect(() => {
    onStateChange?.(tableState);
  }, [onStateChange, tableState]);

  useEffect(() => {
    if (!isFullscreen || !fullscreenContainer) return;

    setActiveFullscreenTable(fullscreenContainer);

    return () => clearActiveFullscreenTable(fullscreenContainer);
  }, [isFullscreen, fullscreenContainer]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),

    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: !!manualPagination,
    rowCount,
    onPaginationChange: (updater) => {
      if (manualPagination) {
        manualPagination.onPaginationChange(
          resolveTableUpdater(updater, manualPagination.pagination),
        );
        return;
      }
      if (containerRef?.current) {
        containerRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
      setPagination(updater);
    },

    getSortedRowModel: getSortedRowModel(),
    manualSorting: !!manualSorting,
    enableMultiSort: !manualSorting,
    onSortingChange: (updater) =>
      manualSorting
        ? manualSorting.onSortingChange(
            resolveTableUpdater(updater, manualSorting.sorting),
          )
        : setTableState((prev) => ({
            ...prev,
            sorting: resolveTableUpdater(updater, prev.sorting ?? []),
          })),
    manualFiltering: !!manualFiltering,
    onGlobalFilterChange: manualFiltering
      ? (updater) =>
          manualFiltering.onSearchChange(
            resolveTableUpdater(updater, manualFiltering.search),
          )
      : setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: globalFilterFn || 'includesString',
    onColumnFiltersChange: (updater) =>
      manualFiltering
        ? manualFiltering.onColumnFiltersChange(
            resolveTableUpdater(updater, manualFiltering.columnFilters),
          )
        : setTableState((prev) => ({
            ...prev,
            columnFilters: resolveTableUpdater(
              updater,
              prev.columnFilters ?? [],
            ),
          })),
    onColumnVisibilityChange: (updater) =>
      setTableState((prev) => ({
        ...prev,
        columnVisibility: resolveTableUpdater(
          updater,
          prev.columnVisibility ?? {},
        ),
      })),

    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpandedRows,

    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),

    autoResetPageIndex: false,

    state: {
      ...tableState,
      pagination: manualPagination?.pagination ?? pagination,
      globalFilter: manualFiltering?.search ?? globalFilter,
      ...(manualFiltering
        ? { columnFilters: manualFiltering.columnFilters }
        : {}),
      ...(manualSorting ? { sorting: manualSorting.sorting } : {}),
      expanded: expandedRows,
    },

    filterFns: {
      arrIncludesArrayItem,
      numberInRange,
    },
    ...rest,
  });

  useEffect(() => {
    if (expanded) {
      setExpandedRows(expanded);
    }
  }, [expanded]);

  const effectiveState = table.getState();
  const goToPage = useCallback(
    (pageIndex: number) => table.setPageIndex(pageIndex),
    [table],
  );
  const goToFirstPage = useCallback(() => goToPage(0), [goToPage]);
  // With server pages the caller resets in the same update as the change;
  // doing it here a render later would send one request for a stale page.
  useFirstPageOnChange(
    manualPagination
      ? null
      : {
          search: manualFiltering?.search ?? globalFilter,
          columnFilters: effectiveState.columnFilters,
          sorting: effectiveState.sorting,
        },
    effectiveState.pagination.pageIndex,
    goToFirstPage,
  );
  useNearestValidPage(
    manualPagination && rowCount === undefined
      ? undefined
      : table.getPageCount(),
    effectiveState.pagination.pageIndex,
    goToPage,
  );

  const exportTableData = useCallback(() => {
    if (exportDisabled) return;
    if (exportFn) {
      exportFn(table.getPrePaginationRowModel().rows, columns);
    } else {
      handleExport(
        table.getPrePaginationRowModel().rows,
        columns,
        exportOptions,
      );
    }
  }, [columns, exportDisabled, exportFn, exportOptions, table]);

  useImperativeHandle(
    ref,
    (): TableRef<TData> => ({
      table,
      tableId,
      exportTableData,
    }),
    [table, tableId, exportTableData],
  );

  const contextValue: TableContextType<TData> = {
    isFullscreen,
    setIsFullscreen,
    globalFilter: manualFiltering?.search ?? globalFilter,
    setGlobalFilter: manualFiltering
      ? (updater) =>
          manualFiltering.onSearchChange(
            resolveTableUpdater(updater, manualFiltering.search),
          )
      : setGlobalFilter,
    tableState: {
      ...tableState,
      ...(manualFiltering
        ? { columnFilters: manualFiltering.columnFilters }
        : {}),
      ...(manualSorting ? { sorting: manualSorting.sorting } : {}),
    },
    table,
    fullscreenContainer,
  };

  function sort(header: Header<TData, unknown>) {
    header.column.toggleSorting(
      header.column.getIsSorted()
        ? header.column.getIsSorted() === 'asc'
        : true,
    );
  }

  const tableContent = (
    <TooltipContainerContext.Provider value={container}>
      <div
        ref={setFullscreenContainer}
        className={cn(
          'eye:flex eye:flex-col',
          isFullscreen
            ? 'eye:grow-y eye:fixed eye:inset-[0] eye:z-10000 eye:bg-grey-10 eye:p-xs eye:[--label-surface:var(--background-color-grey-10)]'
            : 'eye:w-full',
        )}
      >
        {children}
        <div
          className={cn(
            'eye:rounded-md eye:border eye:border-grey-40',
            'eye:w-full eye:flex-1 eye:overflow-x-auto',
          )}
        >
          <div
            ref={containerRef}
            className={cn(
              !isFullscreen && 'eye:max-h-[70dvh]',
              'eye:w-full eye:overflow-y-auto',
              containerClassName,
            )}
          >
            <TablePrimitive
              id={`${id}-table`}
              className={cn('eye:w-full', className)}
            >
              <TableHeader className={'eye:sticky eye:top-[0] eye:z-10'}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        style={{
                          width:
                            header.column.columnDef.size ?? header.getSize(),
                          minWidth:
                            header.column.columnDef.minSize ?? header.getSize(),
                          maxWidth:
                            header.column.columnDef.maxSize ?? header.getSize(),
                        }}
                        key={header.id}
                        aria-sort={
                          header.column.getCanSort()
                            ? getAriaSort(header.column.getIsSorted())
                            : undefined
                        }
                      >
                        <span
                          className={cn(
                            'eye:flex eye:w-full eye:items-center eye:overflow-hidden',
                            getFlexAlignmentClass(
                              header.column.columnDef.meta?.align,
                            ),
                          )}
                        >
                          <ConditionalWrapper
                            condition={header.column.getCanSort()}
                            wrapper={(children) => (
                              <Tooltip
                                content={`${header.column.columnDef.header}
                              ${
                                header.column.getIsSorted()
                                  ? header.column.getIsSorted() === 'asc'
                                    ? `(${t('sortedAsc')})`
                                    : `(${t('sortedDesc')})`
                                  : ''
                              }
                              `}
                              >
                                <span
                                  tabIndex={0}
                                  className='eye:flex eye:min-w-0 eye:cursor-pointer eye:items-center eye:truncate'
                                  role='button'
                                  onClick={() => sort(header)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      // prevents page scroll on Space
                                      e.preventDefault();
                                      sort(header);
                                    }
                                  }}
                                >
                                  <span className='eye:truncate'>
                                    {children}
                                  </span>
                                  {header.column.getIsSorted() ? (
                                    header.column.getIsSorted() === 'asc' ? (
                                      <IconArrowNarrowUp
                                        className='eye:shrink-0'
                                        aria-hidden={true}
                                      />
                                    ) : (
                                      <IconArrowNarrowDown
                                        className='eye:shrink-0'
                                        aria-hidden={true}
                                      />
                                    )
                                  ) : null}
                                </span>
                              </Tooltip>
                            )}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </ConditionalWrapper>
                        </span>
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getPaginationRowModel().rows.length ? (
                  table.getPaginationRowModel().rows.map((row) => (
                    <Fragment key={row.id}>
                      <TableRow
                        className='eye:bg-white'
                        data-state={row.getIsSelected() && 'selected'}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            className={cn(
                              getAlignmentClass(
                                cell.column.columnDef.meta?.align,
                              ),
                            )}
                            style={{
                              width:
                                cell.column.columnDef.size ??
                                cell.column.getSize(),
                              minWidth:
                                cell.column.columnDef.minSize ??
                                cell.column.getSize(),
                              maxWidth:
                                cell.column.columnDef.maxSize ??
                                cell.column.getSize(),
                            }}
                            key={cell.id}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                      {renderExpandable(row)}
                    </Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className='eye:p-32 eye:text-center'
                    >
                      {t('noResultsFound')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </TablePrimitive>
          </div>
          {tableContentEndSlot}
        </div>
        {!hideFooter && (
          <Footer
            page={table.getState().pagination.pageIndex + 1}
            pageCount={table.getPageCount()}
            pageSize={table.getState().pagination.pageSize}
            totalItems={
              rowCount ?? table.getPrePaginationRowModel().rows.length
            }
            onPageChange={(page) => table.setPageIndex(page - 1)}
            onPageSizeChange={(size) => table.setPageSize(size)}
            rowsPerPageOptions={rowsPerPageOptions}
            exportTableData={exportTableData}
            disableExport={exportDisabled}
            isFullscreen={isFullscreen}
            fullscreenContainer={fullscreenContainer}
            paginationLabel={paginationLabel}
            id={id}
          />
        )}
      </div>
    </TooltipContainerContext.Provider>
  );

  return (
    <TableContext.Provider value={contextValue as TableContextType<unknown>}>
      {isFullscreen ? createPortal(tableContent, document.body) : tableContent}
    </TableContext.Provider>
  );
}

const TableBase = forwardRef(TableInner) as unknown as TableComponent;

TableBase.Filter = Filter;

export const Table = TableBase;
